/**
 * Landing page analytics (RFC-1577 §6 Phase 3): the prompt's Style library and
 * Project setup options, the enriched prompt submit, and Stories gallery
 * previews and opens.
 *
 * Pre-auth events: session_id only, never user_id / project_id. Every property
 * is an id, a closed enum, a boolean, a count or a bucket — no prompt text,
 * style names, story titles or file names.
 *
 * Every emitter swallows its own errors: telemetry must never throw into, or
 * change, the landing UI.
 */

import type { QueryClient } from '@tanstack/react-query'
import { queryKeys } from '../../api/hooks'
import type { StyleDetail, StyleListResponse, StyleSummary, UserStyleListResponse } from '../../api/styles'
import { countBucket, lengthBucket } from '../../analytics/buckets'
import { track } from '../../analytics/events'
import type {
  LandingPageProjectSetupAdvancedToggledProps,
  LandingPromptOptionActionProps,
  LandingPagePromptSubmittedProps,
  LandingStoryOpenedProps,
  LandingStoryPreviewStartedProps,
  LandingStyleFamily,
} from '../../analytics/generated/analyticsEvents'
import { NO_STYLE_ID } from '../../constants/styles'
import { DEFAULT_ASPECT_RATIO, type AspectRatio } from '../landing/hero-content'
import type { LandingUploadReference } from '../landing/landingUploads'
import { EMPTY_PROJECT_PLAN_DRAFT, type ProjectPlanDraft } from './ProjectPlanModal'
import { createNadDebug } from '../../utils/debugLog'

const log = createNadDebug('dw:analytics-events:landing')

/** The landing's untouched style (LandingPageV2's initial `currentStyle`). */
export const LANDING_DEFAULT_STYLE_ID = NO_STYLE_ID

// --- Emitters ---------------------------------------------------------------

export function trackLandingPromptOptionAction(props: LandingPromptOptionActionProps): void {
  try {
    log({ event: 'prompt-option-action', ...props })
    track('landing_prompt_option_action', props)
  } catch {
    /* telemetry must never throw */
  }
}

export function trackLandingStoryPreviewStarted(props: LandingStoryPreviewStartedProps): void {
  try {
    log({ event: 'story-preview-started', ...props })
    track('landing_story_preview_started', props)
  } catch {
    /* telemetry must never throw */
  }
}

export function trackLandingStoryOpened(props: LandingStoryOpenedProps): void {
  try {
    log({ event: 'story-opened', ...props })
    track('landing_story_opened', props)
  } catch {
    /* telemetry must never throw */
  }
}

/**
 * `landing_page_project_setup_advanced_toggled`: the user clicked the Project
 * setup modal's Advanced disclosure. Not covered by the analytics doc (whose
 * project_setup contract is open / change / reset only, never per-control), so
 * it is its own landing_page_* event and leaves the option-action funnel as is.
 */
export function trackLandingProjectSetupAdvancedToggled(
  props: LandingPageProjectSetupAdvancedToggledProps,
): void {
  try {
    log({ event: 'project-setup-advanced-toggled', ...props })
    track('landing_page_project_setup_advanced_toggled', props)
  } catch {
    /* telemetry must never throw */
  }
}

// --- Style ------------------------------------------------------------------

// STYLE_GROUPS values (constants/styles.ts) → wire enum. A style carries its
// group as one of its `tags`; any other tag is ignored, so user tag text can
// never reach the wire.
const STYLE_FAMILY_BY_GROUP: ReadonlyMap<string, LandingStyleFamily> = new Map([
  ['Live Action Cinema', 'live_action_cinema'],
  ['3D Animation', '3d_animation'],
  ['2D Animation', '2d_animation'],
  ['Stop Motion', 'stop_motion'],
  ['Illustration', 'illustration'],
])

// Prefix of every `queryKeys.styles(locale)` list (and `queryKeys.style(id)`
// details, skipped below), so any locale's cached catalog matches.
const STYLES_QUERY_PREFIX = ['styles']

/** The style family for a public style's tags (`other` when it has no group). */
export function styleFamilyFromTags(tags: readonly string[]): LandingStyleFamily {
  for (const tag of tags) {
    const family = STYLE_FAMILY_BY_GROUP.get(tag)
    if (family) return family
  }
  return 'other'
}

/**
 * Resolve a style id's family from the already-loaded style queries (the hero's
 * style label hook keeps them warm). Never fetches. `userStyle` is the style
 * object StyleLibraryModal hands back for a user-created style. Returns
 * undefined when the style is not in cache (unknown → omit).
 */
export function resolveLandingStyleFamily(
  queryClient: QueryClient,
  styleId: string,
  userStyle?: StyleSummary,
): LandingStyleFamily | undefined {
  try {
    if (styleId === NO_STYLE_ID) return 'none'
    if (userStyle) return 'custom'
    const userStyles = queryClient.getQueryData<UserStyleListResponse>(queryKeys.userStyles)
    if (userStyles?.styles.some((style) => style.id === styleId)) return 'custom'
    const cached = queryClient.getQueriesData<StyleListResponse | StyleDetail>({ queryKey: STYLES_QUERY_PREFIX })
    for (const [, data] of cached) {
      if (!data || !('styles' in data)) continue
      const match = data.styles.find((style) => style.id === styleId)
      if (match) return styleFamilyFromTags(match.tags)
    }
  } catch {
    /* telemetry must never throw */
  }
  return undefined
}

// --- Project setup ----------------------------------------------------------

/** Everything the Project setup modal edits: the draft plus orientation. */
export interface ProjectSetupState {
  draft: ProjectPlanDraft
  aspectRatio: AspectRatio
}

export const DEFAULT_PROJECT_SETUP_STATE: ProjectSetupState = {
  draft: EMPTY_PROJECT_PLAN_DRAFT,
  aspectRatio: DEFAULT_ASPECT_RATIO,
}

/**
 * Number of Project setup settings that differ between two states. Text fields
 * compare trimmed, matching what the create payload sends.
 */
export function projectSetupChangedCount(a: ProjectSetupState, b: ProjectSetupState): number {
  const text = (x: string, y: string) => (x.trim() === y.trim() ? 0 : 1)
  return (
    text(a.draft.title, b.draft.title) +
    text(a.draft.logline, b.draft.logline) +
    text(a.draft.storyline, b.draft.storyline) +
    text(a.draft.tone, b.draft.tone) +
    text(a.draft.structure, b.draft.structure) +
    (a.draft.contentLanguage === b.draft.contentLanguage ? 0 : 1) +
    (a.draft.targetDurationS === b.draft.targetDurationS ? 0 : 1) +
    (a.aspectRatio === b.aspectRatio ? 0 : 1)
  )
}

/**
 * The option action for a Project setup session (open → close), or null when
 * the session committed nothing. Cancel rolls the draft back but orientation
 * live-commits, so a cancelled session can still carry a change.
 */
export function projectSetupCommitProps(
  before: ProjectSetupState,
  after: ProjectSetupState,
  isAuthenticated: boolean,
): LandingPromptOptionActionProps | null {
  const changed = projectSetupChangedCount(before, after)
  if (changed === 0) return null
  const isDefaultAfter = projectSetupChangedCount(DEFAULT_PROJECT_SETUP_STATE, after) === 0
  return {
    option_type: 'project_setup',
    action: isDefaultAfter ? 'reset' : 'change',
    was_default_before: projectSetupChangedCount(DEFAULT_PROJECT_SETUP_STATE, before) === 0,
    changed_setting_count_bucket: countBucket(changed),
    is_authenticated: isAuthenticated,
  }
}

// --- Prompt submit ----------------------------------------------------------

export interface LandingPromptSubmitState {
  promptLength: number
  isAuthenticated: boolean
  styleId: string
  uploads: readonly LandingUploadReference[]
  /** Settings differing from defaults; undefined while Project setup is gated off. */
  projectSetupChangedCount?: number
}

/** `landing_page_prompt_submitted` props from the final committed hero state. */
export function landingPromptSubmittedProps(state: LandingPromptSubmitState): LandingPagePromptSubmittedProps {
  const assetTypes = new Set(state.uploads.map((upload) => upload.type))
  const setupCount = state.projectSetupChangedCount
  return {
    prompt_len: state.promptLength,
    is_authenticated: state.isAuthenticated,
    prompt_length_bucket: lengthBucket(state.promptLength),
    style_id: state.styleId,
    style_is_default: state.styleId === LANDING_DEFAULT_STYLE_ID,
    has_assets: state.uploads.length > 0,
    asset_type_count: assetTypes.size,
    project_setup_changed: setupCount === undefined ? undefined : setupCount > 0,
    changed_setting_count_bucket: setupCount === undefined ? undefined : countBucket(setupCount),
  }
}
